import type { ShipmentRow } from '../../types';

export type ExecutionLegRow = {
  id: string;
  reference: string;
  status: string;
};

const toNonEmptyString = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  return normalized.length > 0 ? normalized : null;
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const getExecutionLegRows = (record: ShipmentRow): ExecutionLegRow[] => {
  const rawLegs = record.execution_legs;
  if (!Array.isArray(rawLegs)) return [];

  return rawLegs
    .map((leg) => {
      if (!isObjectRecord(leg)) return null;

      return {
        id: toNonEmptyString(leg.id) ?? '',
        reference: toNonEmptyString(leg.reference_number) ?? '',
        status: toNonEmptyString(leg.status) ?? '',
      };
    })
    .filter((leg): leg is ExecutionLegRow => leg !== null);
};

/**
 * The list API returns at most five execution-leg preview rows, while
 * `leg_count` is the full active segment total. Use the latter when it is
 * valid so the grid agrees with the detail drawer and still supports older
 * responses that do not include a total.
 */
export const getExecutionLegDisplayCount = (record: ShipmentRow): number => {
  const previewCount = getExecutionLegRows(record).length;
  const total = Number(record.leg_count);

  return Number.isSafeInteger(total) && total >= previewCount
    ? total
    : previewCount;
};

export const shouldShowExecutionLegAccordion = (
  activeLegType: 'CL' | 'EL' | undefined,
  record: ShipmentRow,
  customerLegEnabled: boolean
): boolean =>
  customerLegEnabled &&
  activeLegType === 'CL' &&
  getExecutionLegRows(record).length > 0;

export const getExpandedRowHeight = (
  legCount: number,
  defaultRowHeight: number
): number => {
  if (legCount <= 0) return defaultRowHeight;
  return defaultRowHeight + legCount * 24 + 12;
};

export const isAccordionToggleTarget = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  Boolean(target.closest('.shipment-grid-customer-leg-toggle'));

// Key used to mark injected EL sub-rows in the grid data array.
export const EL_SUB_ROW_PARENT_KEY = '__el_parent_id' as const;
export const EL_SUB_ROW_PARENT_REF_KEY = '__el_parent_ref' as const;
export const EL_SUB_ROW_POSITION_KEY = '__el_child_position' as const;

/** True when the row is an EL sub-row injected by the accordion. */
export const isElSubRow = (row: ShipmentRow): boolean =>
  Boolean((row as Record<string, unknown>)[EL_SUB_ROW_PARENT_KEY]);

/** Returns the parent CL id for an EL sub-row, or undefined. */
export const getElSubRowParentId = (row: ShipmentRow): string | undefined =>
  (row as Record<string, unknown>)[EL_SUB_ROW_PARENT_KEY] as string | undefined;

/** Returns the parent CL reference for an EL sub-row, or undefined. */
export const getElSubRowParentRef = (row: ShipmentRow): string | undefined =>
  (row as Record<string, unknown>)[EL_SUB_ROW_PARENT_REF_KEY] as
    string | undefined;

export const getElSubRowPosition = (
  row: ShipmentRow
): 'single' | 'first' | 'middle' | 'last' =>
  ((row as Record<string, unknown>)[EL_SUB_ROW_POSITION_KEY] as
    'single' | 'first' | 'middle' | 'last') ?? 'middle';

/**
 * Creates ShipmentRow-shaped objects for each EL under `record`,
 * tagged with the parent CL id so the grid can identify them as sub-rows.
 */
export const createElSubRows = (
  record: ShipmentRow,
  parentId: string
): ShipmentRow[] => {
  const rawLegs = Array.isArray(record.execution_legs)
    ? record.execution_legs.filter(isObjectRecord)
    : [];
  const parentRef = String(
    record.reference_number || record.reference || ''
  ).trim();

  return rawLegs.map((leg, index) => {
    const position =
      rawLegs.length === 1
        ? 'single'
        : index === 0
          ? 'first'
          : index === rawLegs.length - 1
            ? 'last'
            : 'middle';

    return {
      ...leg,
      leg_type: 'EL',
      [EL_SUB_ROW_PARENT_KEY]: parentId,
      [EL_SUB_ROW_PARENT_REF_KEY]: parentRef,
      [EL_SUB_ROW_POSITION_KEY]: position,
    } as ShipmentRow;
  });
};
